import { useEffect, useMemo, useRef, useState } from "react";
import { BideshaiBrand } from "./BideshaiBrand";
import { LanguageDropdown } from "./Navbar";
import visasData from "../data/visas.json";
import { useLanguage } from "../i18n/LanguageContext";

const GUIDE_HERO_IMAGE = "/guide-hero.png";

const VISA_META = {
  d7: {
    difficulty: 3,
    difficultyLabel: "Moderate",
    warning:
      "Tax warning: The NHR tax regime closed March 31, 2025. Foreign pensions are now taxed at standard rates (14.5%–48%). Consult a tax advisor before applying.",
    rejectionReasons: [
      "Income proof insufficient — bank statements don't show consistent monthly deposits above €870",
      "No Portuguese bank account or NIF at time of application",
      "Accommodation proof missing or lease not signed before application",
    ],
    about:
      "The D7 is Portugal's most popular visa for retirees and people who earn money without actively working — from pensions, dividends, rental income, or investment returns. You don't need a job offer. You just need to prove you have enough money coming in every month.",
  },
  d8: {
    difficulty: 4,
    difficultyLabel: "Hard",
    warning:
      "Income warning: D8 requires consistent foreign income above €3,480/month. Income from Portuguese clients or employers can disqualify the application.",
    rejectionReasons: [
      "Income history inconsistent or below the threshold during the review period",
      "Working for or contracting with Portuguese companies instead of foreign ones",
      "Weak proof of remote work relationship, client contracts, or recurring deposits",
    ],
    about:
      "The D8 Digital Nomad Visa is for remote workers employed by or contracting for companies outside Portugal. Portugal uses this route to attract higher-income workers who can support themselves without entering the local labor market.",
  },
  d2_entrepreneur: {
    difficulty: 4,
    difficultyLabel: "Hard",
    warning:
      "Business plan warning: Generic or weak plans are one of the main reasons D2 applications fail. Your plan needs a credible revenue model and a clear Portuguese economic contribution.",
    rejectionReasons: [
      "Business plan too weak, generic, or not adapted to Portugal",
      "Insufficient proof of startup capital and personal financial runway",
      "No clear revenue model, target market, or economic contribution",
    ],
    about:
      "The D2 Entrepreneur Visa is for founders, consultants, and independent professionals building or relocating a business in Portugal. Unlike the D8, it allows you to work directly with Portuguese clients and companies.",
  },
  d2_startup: {
    difficulty: 5,
    difficultyLabel: "Very Hard",
    warning:
      "Startup warning: This route is for genuinely innovative ventures. Lifestyle businesses, agencies, and standard consulting offers usually fit the regular D2 route better.",
    rejectionReasons: [
      "Idea not considered innovative or scalable by IAPMEI evaluators",
      "No formal incubator acceptance from an eligible Portuguese incubator",
      "Insufficient founder funds to sustain the project during early growth",
    ],
    about:
      "The D2 Startup Visa is aimed at high-growth, innovation-led ventures with international potential. It is more selective than the standard D2 and depends heavily on incubator backing and IAPMEI approval.",
  },
  d3: {
    difficulty: 3,
    difficultyLabel: "Moderate",
    warning:
      "Employer warning: The employer must correctly handle the labor market and qualification requirements. A good offer letter alone is not enough if the filing is weak.",
    rejectionReasons: [
      "Salary below the required threshold for highly qualified work",
      "Qualifications not accepted or not clearly documented",
      "Employer did not correctly handle the IEFP or exemption process",
    ],
    about:
      "The D3 is for highly qualified professionals with a Portuguese job offer, often in IT, healthcare, engineering, or other shortage roles. It is also the path into the EU Blue Card framework.",
  },
  d1: {
    difficulty: 2,
    difficultyLabel: "Easy",
    warning:
      "Contract warning: D1 depends on the employer doing their part. If the company will not run the IEFP process or cannot offer a compliant contract, the application usually stops there.",
    rejectionReasons: [
      "Employer refuses or fails to complete the IEFP labor market test",
      "Employment contract shorter than the required minimum duration",
      "Application strategy assumes in-country conversion that is no longer allowed",
    ],
    about:
      "The D1 is Portugal's standard employed worker visa. It works for a wide range of roles as long as there is a compliant Portuguese employment contract and the employer completes the required labor-market steps.",
  },
  d4: {
    difficulty: 2,
    difficultyLabel: "Easy",
    warning:
      "Document warning: The institution and course must be recognized, and accommodation and funds need to be locked in before the consulate stage.",
    rejectionReasons: [
      "Insufficient proof of funds for the study period",
      "Course or institution not recognized by Portuguese authorities",
      "No confirmed accommodation in Portugal",
    ],
    about:
      "The D4 Student Visa is for university students, long language-course students, researchers, and vocational learners. It remains one of the most accessible pathways because study time counts toward long-term residency and citizenship.",
  },
  d6: {
    difficulty: 3,
    difficultyLabel: "Moderate",
    warning:
      "Sponsor warning: The resident in Portugal must keep an active permit and prove enough income for every dependent during the entire process.",
    rejectionReasons: [
      "Sponsor income too low for the total family size",
      "Relationship documents not apostilled or officially translated",
      "Marriage or birth records not accepted to Portuguese legal standards",
    ],
    about:
      "The D6 Family Reunification Visa allows spouses, children, and other eligible relatives of legal residents to join them in Portugal. The process begins inside Portugal with the sponsoring resident, then moves to the consulate abroad.",
  },
  golden_visa: {
    difficulty: 2,
    difficultyLabel: "Easy",
    warning:
      "Investment warning: Real estate no longer qualifies. You must use an eligible route and document the source of funds cleanly from start to finish.",
    rejectionReasons: [
      "Source of funds unclear or insufficiently documented",
      "Fund not properly regulated or otherwise not eligible",
      "Incomplete file after fees or investment steps are already started",
    ],
    about:
      "The Golden Visa is the investment-based path to Portuguese residency with minimal physical presence requirements. It is financially demanding but remains attractive for people who want long-term optionality in Portugal and the EU.",
    investmentRoutes: [
      "Investment fund (CMVM-regulated): €500,000",
      "Cultural heritage donation: €250,000",
      "Scientific research: €500,000",
      "Company plus job creation route: €500,000",
    ],
  },
};

const VISA_ORDER = [
  { key: "d7", code: "D7", label: "Passive Income" },
  { key: "d8", code: "D8", label: "Digital Nomad" },
  { key: "d2_entrepreneur", code: "D2", label: "Entrepreneur" },
  { key: "d3", code: "D3", label: "Highly Qualified" },
  { key: "d1", code: "D1", label: "Employed Worker" },
  { key: "d4", code: "D4", label: "Student" },
  { key: "d6", code: "D6", label: "Family" },
  { key: "golden_visa", code: "ARI", label: "Golden Visa" },
];

const EXTRA_SECTIONS = [
  { key: "aima", label: "Aima" },
  { key: "citizenship", label: "Citizenship" },
];

function getGuideSectionId(key, code) {
  if (key === "golden_visa" || code === "ARI") return "ari";
  if (code) return code.toLowerCase();
  return key;
}

function getGoldenVisaData() {
  const cultural = visasData.visas.golden_visa_cultural;
  const fund = visasData.visas.golden_visa_fund;
  return {
    ...cultural,
    id: "golden_visa",
    code: "ARI",
    name: "Golden Visa",
    tagline: "Residency through qualifying capital investment with low physical presence requirements",
    process: [
      "Choose an eligible route and complete legal due diligence before moving funds",
      "Open a Portuguese bank account and obtain a Portuguese NIF",
      "Complete the investment and collect full documentary proof",
      "Submit the online application and pay the government processing fee",
      "Attend biometrics and wait for residence card issuance",
    ],
    costs: {
      ...cultural.costs,
      total_estimated: "€250,000–€500,000+ plus government and legal fees",
    },
    timeline: {
      ...cultural.timeline,
      total_estimated: "12–40 months",
      note: "Backlog improving in 2026",
    },
    documents_required: [...new Set([...cultural.documents_required, ...fund.documents_required])],
    path_to_citizenship: cultural.path_to_citizenship,
  };
}

function getVisaData(visaKey) {
  return visaKey === "golden_visa" ? getGoldenVisaData() : visasData.visas[visaKey];
}

function getIncomeValue(visaKey, visa) {
  if (visaKey === "golden_visa") return "€250k–€500k";
  if (visaKey === "d2_entrepreneur" || visaKey === "d2_startup") return "No fixed minimum";
  const income = visa.eligibility?.income_thresholds?.main_applicant_monthly;
  if (income) return `€${income.toLocaleString()}/month`;
  const salary = visa.eligibility?.salary_thresholds?.minimum_monthly;
  if (salary) return `€${salary.toLocaleString()}/month`;
  const proof = visa.eligibility?.financial_proof?.minimum_monthly;
  if (proof) return `€${proof.toLocaleString()}/month`;
  return "Varies";
}

function getVisaCards() {
  return VISA_ORDER.map((item) => {
    const visa = getVisaData(item.key);
    const meta = VISA_META[item.key];
    const facts = [
      {
        label: item.key === "golden_visa" ? "Min investment" : "Min income",
        value: getIncomeValue(item.key, visa),
        sub:
          item.key === "d7"
            ? "+50% per adult dependent"
            : item.key === "d8"
              ? "4× Portuguese minimum wage"
              : item.key === "golden_visa"
                ? "Investment route dependent"
                : undefined,
      },
      {
        label: "Processing time",
        value: visa.timeline?.total_estimated || "Varies",
        sub:
          visa.timeline?.consulate_visa_processing
            ? `Official: ${visa.timeline.consulate_visa_processing}`
            : visa.timeline?.note,
      },
      {
        label: "Estimated cost",
        value: visa.costs?.total_estimated || "Varies",
        sub: item.key === "golden_visa" ? "Excludes investment management fees" : "Excl. living costs",
      },
      {
        label: "Path to citizenship",
        value: `${visa.path_to_citizenship?.citizenship_after_years || 5} years`,
        sub: "A2 Portuguese required",
      },
    ];

    return {
      ...item,
      visa,
      meta,
      facts,
      steps: (visa.process || []).slice(0, 5),
    };
  });
}

function GuideHeader({ onStart, onBack }) {
  const { t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <header className="guide-header">
        <div className="guide-header__inner">
          <div className="guide-header__left">
            <button type="button" className="guide-header__logo-button" onClick={onBack} aria-label="Go to home">
              <BideshaiBrand height={37} color="#0E525D" />
            </button>
            <button type="button" className="guide-header__back-link" onClick={onBack}>
              {t("nav_back")}
            </button>
          </div>
          {/* Desktop CTA */}
          <button type="button" className="guide-gradient-button guide-gradient-button--solid guide-header__desktop-cta" onClick={onStart}>
            {t("nav_cta")}
          </button>
          {/* Mobile hamburger */}
          <button
            className="lp-hamburger guide-header__hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Toggle menu"
          >
            <span className="lp-hamburger-line" />
            <span className="lp-hamburger-line" />
            <span className="lp-hamburger-line" />
          </button>
        </div>

        {/* Mobile dropdown */}
        <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`} style={{ top: 64 }}>
          <div className="lp-mobile-lang-switcher-wrap">
            <LanguageDropdown />
          </div>
          <div className="lp-mobile-link" onClick={() => { setMenuOpen(false); onBack(); }}>
            {t("nav_back")}
          </div>
          <div className="lp-mobile-cta">
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onStart(); }}
              style={{
                background: "#1ABC9C",
                color: "#0B2E28",
                border: "none",
                borderRadius: 999,
                padding: "14px 24px",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Outfit', sans-serif",
                width: "100%",
              }}
            >
              {t("nav_cta")}
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 98, background: "rgba(0,0,0,0.2)" }}
        />
      )}
    </>
  );
}

function GuideHero() {
  const stats = [
    { value: "8", label: "Visa categories" },
    { value: "5 yrs", label: "Path to citizenship" },
    { value: "2026", label: "Last verified" },
    { value: "Free", label: "No registration" },
  ];

  return (
    <section className="guide-hero-section">
      <div className="guide-hero">
        <img className="guide-hero__image" src={GUIDE_HERO_IMAGE} alt="" />
        <div className="guide-hero__overlay" />
        <div className="guide-hero__content">
          <div className="guide-hero__content-inner">
          <div className="guide-hero__badge">Updated April 2026</div>
          <h1 className="guide-hero__title">Portugal&apos;s Complete Visa &amp; Residence Permit Guide</h1>
          <p className="guide-hero__description">
            Everything you need to know about moving to Portugal legally &mdash; eligibility, costs,
            timelines, and honest difficulty ratings.
          </p>
          <div className="guide-hero__stats">
            {stats.map((stat) => (
              <div key={stat.label} className="guide-hero__stat">
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GuideTabs({ activeId, onSelect }) {
  return (
    <nav className="guide-tabs" aria-label="Visa guide sections">
      <div className="guide-tabs__track">
        {VISA_ORDER.map((item) => {
          const sectionId = getGuideSectionId(item.key, item.code);
          const active = activeId === sectionId;
          return (
            <button
              key={item.key}
              type="button"
              className={`guide-tabs__item${active ? " is-active" : ""}`}
              onClick={() => onSelect(sectionId)}
            >
              <span className="guide-tabs__code">{item.code}</span>
              <span>{item.label}</span>
            </button>
          );
        })}
        {EXTRA_SECTIONS.map((item) => {
          const active = activeId === item.key;
          return (
            <button
              key={item.key}
              type="button"
              className={`guide-tabs__item guide-tabs__item--plain${active ? " is-active" : ""}`}
              onClick={() => onSelect(item.key)}
            >
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function DifficultyDots({ value }) {
  return (
    <div className="guide-difficulty">
      <span className="guide-difficulty__label">Difficulty</span>
      <div className="guide-difficulty__dots" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, index) => (
          <span key={index} className={index < value ? "is-filled" : ""} />
        ))}
      </div>
    </div>
  );
}

function FactRow({ facts }) {
  return (
    <div className="guide-card__facts">
      {facts.map((fact) => (
        <div key={fact.label} className="guide-card__fact">
          <span>{fact.label}</span>
          <strong>{fact.value}</strong>
          {fact.sub ? <small>{fact.sub}</small> : null}
        </div>
      ))}
    </div>
  );
}

function ProcessList({ steps }) {
  return (
    <ol className="guide-process">
      {steps.map((step, index) => (
        <li key={step} className="guide-process__item">
          <span className="guide-process__number">{index + 1}</span>
          <span>{step}</span>
        </li>
      ))}
    </ol>
  );
}

function VisaCard({ card, onStart }) {
  const sectionLabel = card.key === "golden_visa" ? "Available investment routes" : "Step by step process";
  const sectionId = getGuideSectionId(card.key, card.code);

  return (
    <section id={sectionId} className="guide-card guide-observed">
      <div className="guide-card__header">
        <div className="guide-card__title-group">
          <div className="guide-card__badge">{card.code}</div>
          <h2>{card.visa.name}</h2>
          <p>{card.visa.tagline}</p>
        </div>
        <div className="guide-card__difficulty">
          <DifficultyDots value={card.meta.difficulty} />
          <div className="guide-card__difficulty-label">{card.meta.difficultyLabel}</div>
        </div>
      </div>

      <FactRow facts={card.facts} />

      <div className="guide-card__body">
        <div className="guide-card__column">
          <div className="guide-card__section-title">
            <span>About this visa</span>
          </div>
          <p className="guide-card__body-copy">{card.meta.about}</p>

          {card.key === "golden_visa" ? (
            <ul className="guide-card__route-list">
              {card.meta.investmentRoutes.map((route) => (
                <li key={route}>{route}</li>
              ))}
            </ul>
          ) : (
            <div className="guide-card__warning">{card.meta.warning}</div>
          )}
        </div>

        <div className="guide-card__column">
          <div className="guide-card__section-title">
            <span>{sectionLabel}</span>
          </div>
          {card.key === "golden_visa" ? (
            <ProcessList steps={card.steps} />
          ) : (
            <ProcessList steps={card.steps} />
          )}
        </div>
      </div>

      <div className="guide-card__rejections">
        <div className="guide-card__rejections-title">Top 3 reasons {card.code} applications get rejected</div>
        {card.meta.rejectionReasons.map((reason, index) => (
          <div key={reason} className="guide-card__rejection-item">
            <span>{index + 1}.</span>
            <span>{reason}</span>
          </div>
        ))}
      </div>

      <button type="button" className="guide-gradient-button guide-gradient-button--ghost" onClick={onStart}>
        Check if I qualify for {card.code} &rarr;
      </button>
    </section>
  );
}

function AimaSection() {
  return (
    <section id="aima" className="guide-panel guide-observed">
      <h2>Understanding AIMA</h2>
      <p>
        AIMA (Ag&ecirc;ncia para a Integra&ccedil;&atilde;o, Migra&ccedil;&otilde;es e Asilo) replaced SEF in October 2023
        and handles all Portuguese residence permits. Here&apos;s the current reality:
      </p>
      <div className="guide-panel__grid guide-panel__grid--four">
        <div className="guide-mini-card">
          <span>CPLP nationals</span>
          <strong>30&ndash;45 days</strong>
          <small>Priority processing channel</small>
        </div>
        <div className="guide-mini-card">
          <span>Work permits</span>
          <strong>3&ndash;6 months</strong>
          <small>D1, D2, D3 categories</small>
        </div>
        <div className="guide-mini-card">
          <span>Golden Visa</span>
          <strong>12&ndash;40 months</strong>
          <small>Backlog improving in 2026</small>
        </div>
        <div className="guide-mini-card">
          <span>Grace period</span>
          <strong>6 months</strong>
          <small>Keep expired card plus renewal proof</small>
        </div>
      </div>
    </section>
  );
}

function CitizenshipSection() {
  return (
    <section id="citizenship" className="guide-panel guide-panel--split guide-observed">
      <div className="guide-panel__content">
        <div className="guide-panel__pill">Citizenship</div>
        <h2>The 5-year path to Portuguese citizenship</h2>
        <p>
          As of April 2026, the 5-year residency requirement for Portuguese citizenship still applies.
          Parliament voted to extend this to 10 years in October 2025, but the Constitutional Court
          blocked key provisions in December 2025. Monitor this closely and file as soon as you qualify.
        </p>
      </div>
      <div className="guide-panel__metrics">
        <div className="guide-metric-card">
          <strong>5 yrs</strong>
          <span>Current requirement</span>
        </div>
        <div className="guide-metric-card">
          <strong>A2</strong>
          <span>Portuguese level needed</span>
        </div>
      </div>
    </section>
  );
}

function CommonRequirementsSection() {
  return (
    <section id="common-requirements" className="guide-panel guide-observed">
      <h2>Documents required for every visa</h2>
      <p>
        Regardless of which visa you apply for, these documents are required for all applications.
      </p>
      <div className="guide-panel__grid guide-panel__grid--three">
        {visasData.common_requirements.documents.map((doc) => (
          <div key={doc.name} className="guide-document-card">
            <strong>{doc.name}</strong>
            <span>{doc.detail}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function FooterCta({ onStart }) {
  return (
    <section className="guide-footer-cta">
      <h2>Not sure which visa is right for you?</h2>
      <p>Answer 3 simple questions and find out in under 2 minutes. Free, no registration required.</p>
      <button type="button" className="guide-gradient-button guide-gradient-button--solid" onClick={onStart}>
        Check my visa eligibility &rarr;
      </button>
    </section>
  );
}

function GuideFooter() {
  const { t } = useLanguage();

  return (
    <>
      <section className="lp-section-disclaimer" style={{
        background: "radial-gradient(ellipse at center, #357968 0%, #216663 50%, #0e525d 100%)",
        padding: "80px clamp(24px, 12vw, 261px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 28,
        boxSizing: "border-box",
        textAlign: "center",
      }}>
        <BideshaiBrand height={44} color="#FFFFFF" />
        <p style={{
          margin: 0,
          fontSize: "clamp(14px, 1.2vw, 18px)",
          fontWeight: 300,
          color: "rgba(255,255,255,0.8)",
          lineHeight: "28px",
          letterSpacing: "0.36px",
          maxWidth: 860,
        }}>
          {t("footer_legal")}
        </p>
      </section>

      <footer className="lp-site-footer" style={{
        background: "#ffffff",
        padding: "16px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderTop: "1px solid #f0f0f0",
      }}>
        <p className="lp-site-footer-text" style={{
          margin: 0,
          fontSize: 14,
          fontWeight: 300,
          color: "#738c8b",
          lineHeight: "28px",
          letterSpacing: "0.28px",
          textAlign: "center",
        }}>
          {t("footer_built")}
        </p>
      </footer>
    </>
  );
}

export default function GuidePage({ initialSection, onStart, onBack }) {
  const cards = useMemo(() => getVisaCards(), []);
  const [activeId, setActiveId] = useState(initialSection || "d7");
  const observerRef = useRef(null);

  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll(".guide-observed"));
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (!visible) return;
        const id = visible.target.id;
        setActiveId(id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0.05 }
    );

    nodes.forEach((node) => observerRef.current.observe(node));
    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    if (!initialSection) return;
    requestAnimationFrame(() => {
      document.getElementById(initialSection)?.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(initialSection);
    });
  }, [initialSection]);

  function handleSelect(key) {
    const target = document.getElementById(key);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (key === "aima") {
      document.getElementById("aima")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (key === "citizenship") {
      document.getElementById("citizenship")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <div className="guide-page">
      <GuideHeader onStart={onStart} onBack={onBack} />
      <GuideHero />
      <main className="guide-main">
        <GuideTabs activeId={activeId} onSelect={handleSelect} />
        <div className="guide-content">
          {cards.map((card) => (
            <VisaCard key={card.key} card={card} onStart={onStart} />
          ))}
          <AimaSection />
          <CitizenshipSection />
          <CommonRequirementsSection />
          <FooterCta onStart={onStart} />
        </div>
      </main>
      <GuideFooter />
    </div>
  );
}
